import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import es from './es';
import en from './en';
import pt from './pt';
import { resolveCurrency } from '../utils/fx';

// Divisa de visualización auto-detectada por país (geo-IP). La consume
// CurrencyContext en su estado inicial. Clave distinta de la elección manual
// del usuario (troly_currency), que siempre tiene prioridad.
export const GEO_CURRENCY_KEY = 'troly_currency_geo';

// País detectado por geo-IP (ISO alpha-2). Lo consume la AnnouncementBar para
// pedir el anuncio segmentado por país. Clave propia, cacheada por navegador.
export const GEO_COUNTRY_KEY = 'troly_country_geo';

export const SUPPORTED_LNGS = ['es', 'en', 'pt'];
export const LNG_LABELS = { es: 'Español', en: 'English', pt: 'Português' };

const ES_COUNTRIES = new Set([
  'ES', 'MX', 'AR', 'BO', 'CL', 'CO', 'CR', 'CU', 'DO', 'EC',
  'SV', 'GT', 'HN', 'NI', 'PA', 'PY', 'PE', 'PR', 'UY', 'VE',
]);
const PT_COUNTRIES = new Set(['BR', 'PT']);

export function countryToLanguage(country) {
  if (!country) return 'en';
  const c = String(country).toUpperCase();
  if (ES_COUNTRIES.has(c)) return 'es';
  if (PT_COUNTRIES.has(c)) return 'pt';
  return 'en';
}

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: { es, en, pt },
    // Inglés como idioma internacional por defecto: si la geo-IP falla o
    // excede el timeout, el mundo no-hispano ve inglés, no español.
    fallbackLng: 'en',
    supportedLngs: SUPPORTED_LNGS,
    interpolation: { escapeValue: false },
    detection: {
      // Solo localStorage: el idioma del navegador NO decide.
      // La primera visita la resuelve detectLanguageByGeo() por geo-IP.
      order: ['localStorage'],
      caches: ['localStorage'],
      lookupLocalStorage: 'i18nextLng',
    },
  });

export async function detectLanguageByGeo() {
  // Solo corre una vez por navegador. NO chequeamos i18nextLng aquí: el
  // detector lo cachea automáticamente durante el init con el fallback, así
  // que no sirve para distinguir "el usuario eligió" de "se auto-detectó".
  // i18nGeoTried es nuestra bandera propia y protege la elección manual del
  // usuario en visitas futuras (geo no vuelve a correr).
  if (localStorage.getItem('i18nGeoTried')) return null;
  localStorage.setItem('i18nGeoTried', '1');
  try {
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), 1500);
    const res = await fetch('https://ipapi.co/country/', { cache: 'no-store', signal: ctrl.signal });
    clearTimeout(timer);
    if (!res.ok) return null;
    const country = (await res.text()).trim();
    const lng = countryToLanguage(country);
    if (SUPPORTED_LNGS.includes(lng)) {
      if (lng !== i18n.language) await i18n.changeLanguage(lng);
      else localStorage.setItem('i18nextLng', lng); // persistir aunque coincida con el fallback
    }
    // Persistir la divisa del país para que CurrencyContext la use por defecto.
    // Corre antes del primer render (index.js), así que el provider la lee al montar.
    try { localStorage.setItem(GEO_CURRENCY_KEY, resolveCurrency({ country, lang: lng })); } catch { /* ignore */ }
    // Persistir también el país para la geo-segmentación de anuncios.
    try { if (country) localStorage.setItem(GEO_COUNTRY_KEY, country); } catch { /* ignore */ }
    return lng;
  } catch {
    return null;
  }
}

// País del visitante para segmentar anuncios. Usa el valor cacheado por
// detectLanguageByGeo; si falta (visitante recurrente anterior a esta función),
// lo resuelve una sola vez por geo-IP y lo cachea. Nunca rechaza.
export async function getGeoCountry() {
  try {
    const cached = localStorage.getItem(GEO_COUNTRY_KEY);
    if (cached) return cached.toUpperCase();
  } catch { /* ignore */ }
  try {
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), 1500);
    const res = await fetch('https://ipapi.co/country/', { cache: 'no-store', signal: ctrl.signal });
    clearTimeout(timer);
    if (!res.ok) return null;
    const country = (await res.text()).trim().toUpperCase();
    if (country) { try { localStorage.setItem(GEO_COUNTRY_KEY, country); } catch { /* ignore */ } }
    return country || null;
  } catch {
    return null;
  }
}

export default i18n;
